import { EMPTY_POD_NOTES } from "./notesUtils";

export const NOTE_KEYS = Object.keys(EMPTY_POD_NOTES);

export function isEmptyNotesValue(value) {
  if (value == null) return true;
  if (typeof value === "boolean") return false;
  return String(value).trim() === "";
}

export function valuesEqual(a, b) {
  if (typeof a === "boolean" || typeof b === "boolean") {
    return Boolean(a) === Boolean(b);
  }

  return String(a ?? "").trim() === String(b ?? "").trim();
}

export function normalizeUsername(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

export function localUserHasSessionPriority(username, podSession) {
  const normalized = normalizeUsername(username);
  if (!normalized) return false;

  return (
    normalized === normalizeUsername(podSession?.proctor) ||
    normalized === normalizeUsername(podSession?.transferToProctor) ||
    normalized === normalizeUsername(podSession?.transferTo)
  );
}

function resolveConflictValue(localValue, remoteValue, localHasPriority) {
  if (valuesEqual(localValue, remoteValue)) return localValue;
  if (isEmptyNotesValue(localValue)) return remoteValue;
  if (isEmptyNotesValue(remoteValue)) return localValue;
  return localHasPriority ? localValue : remoteValue;
}

/**
 * Last-write-wins for subscription updates: patch only keys that changed
 * between the previous and next remote snapshots. Unrelated local edits stay.
 * Keys still being edited locally (dirty) are not overwritten.
 */
export function applyRemotelyChangedNotes(
  localNotes,
  previousRemoteNotes,
  nextRemoteNotes,
  { dirtyKeys } = {},
) {
  const dirty =
    dirtyKeys instanceof Set ? dirtyKeys : new Set(dirtyKeys ?? []);
  const merged = { ...localNotes };
  const changedKeys = [];

  NOTE_KEYS.forEach((key) => {
    if (dirty.has(key)) return;

    const previousValue = previousRemoteNotes?.[key];
    const nextValue = nextRemoteNotes?.[key];

    if (valuesEqual(previousValue, nextValue)) return;

    merged[key] = nextValue;
    changedKeys.push(key);
  });

  return { notes: merged, changedKeys };
}

/**
 * Apply a remote notes object into local notes without wiping dirty/unsaved work.
 * Never replaces a non-empty local value with an empty remote value.
 */
export function mergeRemoteNotesIntoLocal(
  localNotes,
  remoteNotes,
  { dirtyKeys, localHasPriority = false } = {},
) {
  const dirty = dirtyKeys instanceof Set ? dirtyKeys : new Set(dirtyKeys ?? []);
  const merged = { ...localNotes };

  NOTE_KEYS.forEach((key) => {
    const localValue = localNotes?.[key];
    const remoteValue = remoteNotes?.[key];

    if (dirty.has(key)) {
      // Keep in-progress edits. If remote also has a value and local user
      // lacks priority, still prefer local while dirty so typing isn't yanked.
      merged[key] = resolveConflictValue(localValue, remoteValue, true);
      return;
    }

    if (isEmptyNotesValue(remoteValue) && !isEmptyNotesValue(localValue)) {
      merged[key] = localValue;
      return;
    }

    if (!isEmptyNotesValue(remoteValue) && isEmptyNotesValue(localValue)) {
      merged[key] = remoteValue;
      return;
    }

    if (
      !isEmptyNotesValue(remoteValue) &&
      !isEmptyNotesValue(localValue) &&
      !valuesEqual(localValue, remoteValue)
    ) {
      merged[key] = localHasPriority ? localValue : remoteValue;
      return;
    }

    if (remoteValue !== undefined) {
      merged[key] = remoteValue;
    }
  });

  return merged;
}

/**
 * Build the notes payload for a save: start from latest remote, then overlay
 * only the fields this user has edited (dirty). Dirty fields always use the
 * local value (last-write-wins for the saving user), regardless of proctor
 * assignment.
 */
export function mergeNotesForSave(
  remoteNotes,
  localNotes,
  { dirtyKeys } = {},
) {
  const dirty = dirtyKeys instanceof Set ? dirtyKeys : new Set(dirtyKeys ?? []);
  const merged = { ...remoteNotes };

  dirty.forEach((key) => {
    if (!NOTE_KEYS.includes(key)) return;

    merged[key] = localNotes?.[key];
  });

  return merged;
}

/**
 * Merge a subscription PodSession into local session state.
 * Trusts remote scalars (including intentional clears like unclaim).
 * Notes string is replaced with remote; form-level merge happens separately.
 */
export function mergeRemotePodSession(localSession, remoteSession) {
  if (!localSession) return remoteSession;
  if (!remoteSession) return localSession;

  return {
    ...localSession,
    ...remoteSession,
    // Prefer remote fields array when present; avoid wiping with undefined.
    fields:
      remoteSession.fields !== undefined
        ? remoteSession.fields
        : localSession.fields,
  };
}
